import { Multirange as _Multirange, Range as _Range } from '@attain-apps/range'
import { Word } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../common'
import * as expression from '../expression'
import { Value as _Value } from '../value'

import { Query } from './query'

/**
 * Represents an additional value that can be inputted with and referenced in
 * a {@link Query}
 */
export interface Variable<TType extends _Value.Type = _Value.Type>
  extends common.Identified<Variable.Name>,
    common.Named,
    common.Described.Optionally {
  type: TType
  defaultValue?: unknown
}
export namespace Variable {
  /**
   * Provides values for variables that are referenced in a query
   */
  export type Lookup = Partial<Record<Name, Value>>
  export namespace Lookup {
    export const isLookup: TypeChecker<Lookup> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.record(Name.isName, TypeChecker.isUnknown),
    )

    /**
     * Returns a {@link Lookup} with the default values of the given
     * {@link Variable Variables}
     */
    export function usingDefaults(variables: readonly Variable[]): Lookup {
      return Object.fromEntries(
        variables.map(variable => [variable.id, variable.defaultValue]),
      )
    }
  }

  export type Value = unknown

  /**
   * Name of a variable in a query
   */
  export type Name = Name.UserDefined | Name.Reserved
  export namespace Name {
    export const isName: TypeChecker<Name> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.union(UserDefined.isUserDefined, Reserved.isReserved),
    ).usingAlias('dms.data.query.Variable.Name')

    /**
     * Represents a {@link Variable} defined by an end-user
     */
    export type UserDefined = Word
    export namespace UserDefined {
      export const isUserDefined: TypeChecker<UserDefined> =
        Word.isWord.usingAlias('dms.data.query.Variable.Name.UserDefined')
    }

    /**
     * Represents a {@link Variable} defined by the system
     */
    export type Reserved<TSuffix extends Word = Word> =
      `${typeof Reserved.PREFIX}${TSuffix}`
    export namespace Reserved {
      export const PREFIX = '$'

      export const isReserved: TypeChecker<Reserved> =
        TypeChecker.Factory.custom(
          (val: unknown): val is Reserved =>
            TypeChecker.isString(val) &&
            val.startsWith(PREFIX) &&
            Word.isWord(val.substring(1)),
          {
            typeDescription: 'dms.data.query.Variable.Name.Reserved',
          },
        )

      /**
       * Returns a {@link Variable.Name.Reserved} with the given suffix
       */
      export function from<TSuffix extends Word>(
        suffix: TSuffix,
      ): Reserved<TSuffix> {
        return `${PREFIX}${suffix}`
      }
    }

    /**
     * Aliases of variables declared in the given query, including in nested
     * subqueries
     */

    export function allDeclaredIn(query: Query): Name[] {
      return Array.from(
        new Set(
          Query.allIn(query).flatMap(_query => directlyDeclaredIn(_query)),
        ),
      )
    }

    /**
     * Aliases of variables referenced in the given query, including in nested
     * subqueries
     *
     * __NOTE:__ done as
     * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function* generator}
     * since this is traversing all expressions in the query and nested subqueries
     */
    export function* allReferencedIn(query: Query): Generator<Name> {
      const encountered = new Set<Name>()
      for (const _query of Query.allIn(query)) {
        for (const alias of directlyReferencedIn(_query)) {
          if (!encountered.has(alias)) {
            encountered.add(alias)
            yield alias
          }
        }
      }
    }

    /**
     * Aliases of variables directly declared in the given query (i.e. not
     * including nested subqueries)
     */

    export function directlyDeclaredIn(query: Query): Name[] {
      const { variables = {} } = query
      return Object.keys(variables).filter(
        alias => variables[alias] !== undefined,
      )
    }

    /**
     * Aliases of variables directly referenced in the given query (i.e. not
     * including nested subqueries)
     *
     * __NOTE:__ done as
     * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function* generator}
     * since this is traversing all expressions in the query
     */
    export function* directlyReferencedIn(query: Query): Generator<Name> {
      for (const _expression of expression.Expression.allFrom(query)) {
        if (expression.fn.definitions.GetVariable.isGetVariable(_expression)) {
          yield _expression[expression.fn.Key.GetVariable]
        }
      }
    }
  }

  /**
   * Encodes a {@link Variable} for storage in a database by converting any class instances
   * (such as inline enums) to plain objects. This ensures compatibility with JSON-based
   * storage formats in both SQL and DynamoDB, which cannot persist class instances.
   *
   * The returned value is typed as `any` because the resulting object may differ from the
   * strict {@link Variable} type, especially for fields like `type` that are converted
   * from class instances to plain objects.
   */
  export function encode(variable: Variable): any {
    const { type, defaultValue, ...rest } = variable
    const { inlineEnum } = type as FlattenUnion<typeof type>

    // Serialize defaultValue to ensure DMS instances (DmsDate, DmsRange,
    // DmsMultiRange) are converted to their envelope format ($date, $range,
    // $multirange)
    const serializedDefaultValue =
      defaultValue !== undefined ? _Value.toJson(defaultValue) : undefined

    if (inlineEnum instanceof common.value.types.Enum) {
      return {
        ...rest,
        defaultValue: serializedDefaultValue,
        type: {
          inlineEnum: {
            entries: inlineEnum.entries,
            isMultiselect: inlineEnum.isMultiselect,
          },
        },
      }
    }

    // Otherwise, return the variable with its type as-is
    return {
      ...rest,
      defaultValue: serializedDefaultValue,
      type,
    }
  }

  /**
   * Decodes a variable object retrieved from a database, reconstructing any class instances
   * (such as inline enums) and casting the `defaultValue` to the correct type if necessary.
   *
   * The input is typed as `any` because database records are stored as plain JSON objects,
   * which may not strictly match the {@link Variable} type. This function restores the
   * necessary class instances and type information for use in application logic.
   */
  export function decode(variable: any): Variable {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { type, defaultValue, ...rest } = variable
    const { inlineEnum } = type as FlattenUnion<typeof type>

    // If type is intrinsic, cast the defaultValue to match the type
    if (common.value.types.Intrinsic.isIntrinsic(type)) {
      // Reconstruct DMS instances from their envelope format
      // ($date, $range, $multirange) before casting
      const processedValue = _Value.fromJson(defaultValue)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return {
        ...rest,
        // Cast the value to ensure it matches the intrinsic type
        defaultValue: common.value.Raw.cast(processedValue, type),
        type,
      }
    }

    // Handle inline enums - need to reconstruct the Enum class instance
    if (inlineEnum) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return {
        ...rest,
        defaultValue,
        type: {
          inlineEnum: new common.value.types.Enum({
            entries: inlineEnum.entries,
            isMultiselect: inlineEnum.isMultiselect,
          }),
        },
      }
    }

    // Otherwise, return variable unchanged
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return variable
  }
}
